import { DEFAULT_HEADER_OFFSET, getHeaderOffset } from '@/components/shared/utils/viewportShell'

// 首页滚动重置
const HOME_ENTRY_PENDING_CLASS = 'home-entry-pending'

/** 获取首页锚点滚动时需预留的顶栏高度（实测顶栏） */
export function getHomeHeaderOffset() {
  if (typeof window === 'undefined') return DEFAULT_HEADER_OFFSET
  return getHeaderOffset()
}

const HOME_ENTRY_MIN_HERO_HEIGHT = 180
const HOME_ENTRY_MAX_WAIT_MS = 1200

let activeScrollCancel = null
let homeEntryPending = false
let homeEntryRaf = null

// 清除首页入场等待
function clearHomeEntryTimers() {
  if (homeEntryRaf) {
    cancelAnimationFrame(homeEntryRaf)
    homeEntryRaf = null
  }
}

/** 首页主内容是否已撑起，避免短页时先露出页脚 */
function isHomeLayoutReady() {
  const homePage = document.querySelector('.home-page')
  if (!homePage) return false
  if (homePage.classList.contains('home-page--fallback')) return true

  const hero = document.getElementById('home-hero') || homePage.querySelector('.home-hero')
  if (hero && hero.getBoundingClientRect().height >= HOME_ENTRY_MIN_HERO_HEIGHT) return true

  return Boolean(homePage.querySelector('.home-page__middle'))
}

/** 等 Hero/区块布局完成，或超时后执行回调 */
function waitForHomeLayoutReady(onReady) {
  const start = performance.now()

  function check() {
    homeEntryRaf = null
    if (!homeEntryPending) return

    const timedOut = performance.now() - start >= HOME_ENTRY_MAX_WAIT_MS
    if (isHomeLayoutReady() || timedOut) {
      onReady()
      return
    }

    homeEntryRaf = requestAnimationFrame(check)
  }

  homeEntryRaf = requestAnimationFrame(check)
}

function revealHomeEntry() {
  scrollPageToTop()
  if (!homeEntryPending) return
  document.documentElement.classList.remove(HOME_ENTRY_PENDING_CLASS)
  homeEntryPending = false
}

/** 三次缓动曲线，用于平滑滚动动画 */
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/** 获取当前页面纵向滚动位置 */
function getScrollTop() {
  return window.pageYOffset || document.documentElement.scrollTop || 0
}

/** 平滑滚动到指定纵向位置，支持取消与完成回调 */
export function smoothScrollToY(targetTop, { duration = null, onComplete = null } = {}) {
  if (activeScrollCancel) {
    activeScrollCancel()
    activeScrollCancel = null
  }

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const startTop = getScrollTop()
  const distance = targetTop - startTop

  if (Math.abs(distance) < 2 || prefersReducedMotion) {
    window.scrollTo({ top: targetTop, left: 0, behavior: 'auto' })
    onComplete?.()
    return
  }

  const computedDuration =
    duration ?? Math.min(1100, Math.max(680, Math.abs(distance) * 0.55))

  const startTime = performance.now()
  let rafId = null
  let cancelled = false

  // 是否可
  function cancel() {
    cancelled = true
    if (rafId) cancelAnimationFrame(rafId)
  }

  activeScrollCancel = cancel

  // 平滑滚动动画帧
  function step(now) {
    if (cancelled) return

    const progress = Math.min(1, (now - startTime) / computedDuration)
    const eased = easeInOutCubic(progress)
    const currentTop = startTop + distance * eased

    window.scrollTo({ top: currentTop, left: 0, behavior: 'auto' })

    if (progress < 1) {
      rafId = requestAnimationFrame(step)
      return
    }

    activeScrollCancel = null
    onComplete?.()
  }

  rafId = requestAnimationFrame(step)
}

/** 取消当前进行中的平滑滚动 */
export function cancelSmoothScroll() {
  if (activeScrollCancel) {
    activeScrollCancel()
    activeScrollCancel = null
  }
}

/** 立即将页面滚动到顶部 */
export function scrollPageToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

/** 从其他页面进入首页前：隐藏主内容/页脚并置顶，避免闪到底部或黑屏 */
export function beginHomeEntry() {
  clearHomeEntryTimers()
  homeEntryPending = true
  document.documentElement.classList.add(HOME_ENTRY_PENDING_CLASS)
  scrollPageToTop()
}

/** 离开首页时取消入场隐藏，避免内页页脚被一直藏住 */
export function cancelHomeEntry() {
  clearHomeEntryTimers()
  if (!homeEntryPending) {
    document.documentElement.classList.remove(HOME_ENTRY_PENDING_CLASS)
    return
  }
  document.documentElement.classList.remove(HOME_ENTRY_PENDING_CLASS)
  homeEntryPending = false
}

/** 首页渲染完成后等布局撑起再显示，避免短页先露出页脚 */
export function completeHomeEntry() {
  clearHomeEntryTimers()
  scrollPageToTop()
  window.dispatchEvent(new CustomEvent('home-reset'))

  requestAnimationFrame(() => {
    scrollPageToTop()
    requestAnimationFrame(scrollPageToTop)
  })

  waitForHomeLayoutReady(() => {
    scrollPageToTop()
    requestAnimationFrame(() => {
      scrollPageToTop()
      revealHomeEntry()
    })
  })
}

/** 滚动到首页指定锚点区块，自动扣除顶栏高度 */
export function scrollToHomeAnchor(id, { smooth = true, onComplete = null } = {}) {
  const el = document.getElementById(id)
  if (!el) return false

  const headerHeight = getHomeHeaderOffset()
  const top = Math.max(0, el.getBoundingClientRect().top + getScrollTop() - headerHeight)

  if (smooth) {
    // smooth滚动ToY
    smoothScrollToY(top, { onComplete })
  } else {
    window.scrollTo({ top, left: 0, behavior: 'auto' })
    onComplete?.()
  }
  return true
}

/** 滚动到首页 Hero 之后的第一个内容区块 */
export function scrollToFirstHomeSection({ smooth = true } = {}) {
  const hero = document.getElementById('home-hero') || document.querySelector('.home-hero')
  if (!hero) return false

  // Hero 的下一个兄弟是 home-page__middle（无 id），真正带锚点的是内部第一个 section
  const middle = hero.nextElementSibling
  const firstSection =
    middle?.querySelector?.('section[id]') ||
    middle?.querySelector?.('[id^="home-section-"]') ||
    null

  const sectionId = firstSection?.id
  if (!sectionId) return false
  return scrollToHomeAnchor(sectionId, { smooth })
}

/** 滚动到顶部并派发 home-reset 自定义事件 */
export function dispatchHomeReset() {
  scrollPageToTop()
  window.dispatchEvent(new CustomEvent('home-reset'))
}
